// ============================================================================
// lib/market.ts — КЛИЕНТСКАЯ ВИТРИНА МАРКЕТПЛЕЙСА (hydra.market, mkt_requests).
//
// SSoT данных — сервер: категории — справочник mkt_categories, заявки —
// документ mkt_requests, фото — штатные «Файлы» (file_links: ownerId = id
// заявки). Никаких новых серверных полей/моков: используются только
// существующие реквизиты (category_id, address, descr, phone, status,
// master_id, take_price) и стандартный аудит (created_by/created_at).
//
// ВАЖНО (зафиксировано в отчёте задачи): в поставке hydra.market НЕТ полей
// «район» и «желаемая дата»; район вводится вместе с адресом в address.
// Права объектов решает сервер (роли Мастер/Оператор/Арбитр); клиент без
// права «Добавление» получит явную ошибку доступа — витрина показывает её
// текстом, а не пустой список.
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import {
  marketGeoFromCells,
  type MarketGeoRow,
  type MarketGeoValue,
} from '@/lib/marketGeo';
import { readThrough } from '@/lib/offlineCache';
import {
  friendlyError,
  isNotFoundError,
  isPermissionDeniedError,
  isTransportError,
  logSwallowed,
} from '@/lib/errors';

export {
  MARKET_CLIENT_APP_CODE,
  MARKET_REQUEST_TABLE,
  MARKET_CATEGORY_TABLE,
  MARKET_SERVICE_TABLE,
  MARKET_MASTER_TABLE,
  MARKET_STATUS_ENUM,
} from './marketConstants';

import {
  MARKET_REQUEST_TABLE,
  MARKET_CATEGORY_TABLE,
  MARKET_SERVICE_TABLE,
  MARKET_MASTER_TABLE,
  MARKET_STATUS_ENUM,
} from './marketConstants';

const PAGE_SIZE = 200;

/** Категория услуги (иерархия mkt_categories: группы + элементы). */
export interface MarketCategory {
  id: string;
  code: string;
  name: string;
  parentId: string;
  isGroup: boolean;
}

/** Услуга мастера/витрины (справочник mkt_services: категория + цена от). */
export interface MarketService {
  id: string;
  code: string;
  name: string;
  categoryId: string;
  priceFrom: number | null;
}

/** Заявка клиента (только существующие реквизиты документа mkt_requests). */
export interface MarketRequest {
  id: string;
  number: string;
  categoryId: string;
  categoryName: string;
  masterId: string;
  masterName: string;
  status: string;
  statusLabel: string;
  descr: string;
  address: string;
  phone: string;
  createdAt: string;
  /** Автор заявки (стандартное аудит-поле created_by) — для участников чата. */
  createdBy: string;
  takePrice: string;
  contactRevealed: boolean;
  isMarked: boolean;
  /**
   * Гейт телефона (SSoT — сервер, markettake/phone.go): метки приходят в
   * строке заявки ListTableData. false/false = метки нет (старые записи,
   * заявка без автора) — статус считается неизвестным, а не «подтверждён».
   */
  phoneVerified: boolean;
  phoneUnverified: boolean;
  /** Человеческая подсказка сервера (только у неподтверждённых). */
  phoneVerificationHint: string;
  /**
   * Гео строки: ссылки иерархии и (если сервер раскрыл) координаты/радиус.
   * После оплаченного взятия сервер отдаёт координаты взявшему мастеру.
   */
  geo: MarketGeoRow;
  /** Карточка мастера (id в mkt_masters) — сервер резолвит master_id. */
  masterCatalogId: string;
  /** Средняя оценка мастера (только опубликованные отзывы; null — нет данных). */
  masterRating: number | null;
  /** Количество опубликованных отзывов мастера (null — нет данных). */
  masterReviewsCount: number | null;
}

/** Состояние телефона заявки по серверным меткам. */
export type MarketPhoneState = 'verified' | 'unverified' | 'unknown';

export interface MarketPhoneGate {
  state: MarketPhoneState;
  hint: string;
}

export function marketPhoneState(
  request: Pick<MarketRequest, 'phoneVerified' | 'phoneUnverified'>,
): MarketPhoneState {
  if (request.phoneVerified) return 'verified';
  if (request.phoneUnverified) return 'unverified';
  return 'unknown';
}

/** Телефон заявки блокирует взятие и раскрытие контактов (мастер/карточка). */
export function isMarketPhoneBlocked(
  request: Pick<MarketRequest, 'phoneVerified' | 'phoneUnverified'>,
): boolean {
  return marketPhoneState(request) === 'unverified';
}

function normalizePhoneDigits(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

/** Фото заявки (запись «Файлов», привязанная регистром file_links). */
export interface MarketPhoto {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface CreateMarketRequestInput {
  categoryId: string;
  address: string;
  descr: string;
  phone: string;
  /** Гео-схема заявки (страна/регион/город/район, координаты, радиус поиска). */
  geo?: MarketGeoValue;
}

export interface CreatedMarketRequest {
  id: string;
  /** true — запись поставлена в durable-очередь (нет связи); серверный id ещё неизвестен. */
  queued: boolean;
}

export interface PhotoUploadFailure {
  fileName: string;
  error: string;
}

export interface PhotoUploadResult {
  uploaded: number;
  /** Фото в durable-очереди загрузки (появятся в заявке после восстановления связи). */
  queued: number;
  failures: PhotoUploadFailure[];
}

export type MarketFailureKind = 'permission' | 'not-installed' | 'not-found' | 'offline' | 'other';

export interface MarketFailure {
  kind: MarketFailureKind;
  message: string;
}

// ---------- Служебные преобразования строк --------------------------------------

export function cellsOf(row: TableDataRow): Map<string, string> {
  const cells = new Map<string, string>();
  for (const v of row.values || []) {
    if (v.value === '<nil>') continue;
    cells.set(String(v.column), String(v.value ?? ''));
  }
  return cells;
}

function isTrue(value: string | undefined): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

/** Число из строки сервера; пусто/нечисло — null (не подменяем нулём). */
function numberOrNull(value: string | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Таблица метаданных → строки с постраничной загрузкой (сервер фильтрует). */
export async function listTableRows(
  table: string,
  options: {
    pageSize?: number;
    filters?: Array<{ column: string; value: string }>;
    orderBy?: string;
    orderDesc?: boolean;
  } = {},
): Promise<TableDataRow[]> {
  const pageSize = options.pageSize || PAGE_SIZE;
  const out: TableDataRow[] = [];
  for (let page = 0; page < 100; page++) {
    const req: Record<string, unknown> = {
      schema: 'public',
      table,
      pageSize,
      page,
      includeMarked: false,
      filters: options.filters || [],
    };
    if (options.orderBy) {
      req.orderBy = options.orderBy;
      req.orderDesc = !!options.orderDesc;
    }
    const res: any = await getSDK().metadata.listTableData(req as any);
    const rows = (res.rows || []) as TableDataRow[];
    out.push(...rows);
    const total = Number(res.totalCount || 0);
    if (rows.length < pageSize) break;
    if (total > 0 && out.length >= total) break;
  }
  return out;
}

// ---------- Категории -----------------------------------------------------------

function categoryFromRow(row: TableDataRow): MarketCategory {
  const cells = cellsOf(row);
  return {
    id: row.id,
    code: cells.get('code') || '',
    name: cells.get('name') || cells.get('synonym') || cells.get('code') || 'Без названия',
    parentId: cells.get('parent_id') || '',
    isGroup: isTrue(cells.get('is_group')),
  };
}

/** Категории услуг (все, включая группы; UI выбирает только элементы). */
export async function loadMarketCategories(): Promise<MarketCategory[]> {
  const cached = await readThrough<MarketCategory[]>('market:categories', async () => {
    const rows = await listTableRows(MARKET_CATEGORY_TABLE, {
      orderBy: 'name',
      pageSize: 500,
    });
    return rows
      .map(categoryFromRow)
      .filter((c) => c.id && c.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  });
  return cached.data;
}

function serviceFromRow(row: TableDataRow): MarketService {
  const cells = cellsOf(row);
  return {
    id: row.id,
    code: cells.get('code') || '',
    name: cells.get('name') || cells.get('synonym') || cells.get('code') || 'Без названия',
    categoryId: cells.get('category_id') || '',
    priceFrom: numberOrNull(cells.get('price_from')),
  };
}

/** Услуги витрины (справочник mkt_services; отбор по категории — в интерфейсе). */
export async function loadMarketServices(): Promise<MarketService[]> {
  const cached = await readThrough<MarketService[]>('market:services', async () => {
    const rows = await listTableRows(MARKET_SERVICE_TABLE, {
      orderBy: 'name',
      pageSize: 500,
    });
    return rows
      .map(serviceFromRow)
      .filter((service) => service.id && service.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  });
  return cached.data;
}

// ---------- Статусы -------------------------------------------------------------

/**
 * Подписи статусов — из ПЕРЕЧИСЛЕНИЯ базы (SSoT сервера, а не хардкод).
 * Перечисление не найдено — пустая карта: список покажет КОД статуса (честно),
 * а не выдуманную подпись.
 */
export async function loadMarketStatusLabels(): Promise<Record<string, string>> {
  try {
    const cached = await readThrough<Record<string, string>>('market:status-labels', async () => {
      const res: any = await getSDK().metadata.getEnum({ code: MARKET_STATUS_ENUM });
      const labels: Record<string, string> = {};
      for (const v of (res.values || []) as any[]) {
        const code = String(v.value || v.name || '');
        if (!code) continue;
        labels[code] = String(v.synonym || v.name || code);
      }
      return labels;
    });
    return cached.data;
  } catch (e) {
    if (isNotFoundError(e)) return {};
    throw e;
  }
}

// ---------- Заявки --------------------------------------------------------------

function requestFromRow(
  row: TableDataRow,
  categories: Map<string, MarketCategory>,
  statusLabels: Record<string, string>,
): MarketRequest {
  const cells = cellsOf(row);
  const categoryId = cells.get('category_id') || '';
  const status = cells.get('status') || '';
  const categoryName = cells.get('category_id_presentation')
    || categories.get(categoryId)?.name
    || '';
  return {
    id: row.id,
    number: cells.get('number') || '',
    categoryId,
    categoryName,
    masterId: cells.get('master_id') || '',
    masterName: cells.get('master_id_presentation') || cells.get('master_name') || '',
    status,
    statusLabel: statusLabels[status] || status,
    descr: cells.get('descr') || '',
    address: cells.get('address') || '',
    phone: cells.get('phone') || '',
    createdAt: cells.get('created_at') || '',
    createdBy: cells.get('created_by') || '',
    takePrice: cells.get('take_price') || '',
    contactRevealed: isTrue(cells.get('contact_revealed')),
    isMarked: isTrue(cells.get('is_marked')),
    phoneVerified: isTrue(cells.get('phone_verified')),
    phoneUnverified: isTrue(cells.get('phone_unverified')),
    phoneVerificationHint: cells.get('phone_verification_hint') || '',
    geo: marketGeoFromCells(cells),
    masterCatalogId: cells.get('master_catalog_id') || '',
    masterRating: numberOrNull(cells.get('master_rating')),
    masterReviewsCount: numberOrNull(cells.get('master_reviews_count')),
  };
}

/** Категории/подписи — вспомогательные данные: без них список всё равно читаем. */
async function auxiliaryData(): Promise<{
  categories: Map<string, MarketCategory>;
  statusLabels: Record<string, string>;
}> {
  let categories = new Map<string, MarketCategory>();
  let statusLabels: Record<string, string> = {};
  try {
    const list = await loadMarketCategories();
    categories = new Map(list.map((c) => [c.id, c]));
  } catch (e) {
    logSwallowed(e, 'market: категории для названий заявок');
  }
  try {
    statusLabels = await loadMarketStatusLabels();
  } catch (e) {
    logSwallowed(e, 'market: подписи статусов заявок');
  }
  return { categories, statusLabels };
}

/**
 * «Мои заявки» — заявки, созданные текущим пользователем. Отдельного RPC
 * «мои заявки» в поставке нет: штатный серверный отбор по created_by
 * (стандартное аудит-поле документа), дополнительная проверка — в клиенте.
 */
export async function loadMyMarketRequests(userId: string): Promise<MarketRequest[]> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const cached = await readThrough<MarketRequest[]>(`market:requests:${userId}`, async () => {
    const aux = await auxiliaryData();
    const rows = await listTableRows(MARKET_REQUEST_TABLE, {
      pageSize: 200,
      orderBy: 'created_at',
      orderDesc: true,
      filters: [{ column: 'created_by', value: userId }],
    });
    return rows
      .filter((row) => {
        const cells = cellsOf(row);
        return (cells.get('created_by') || '') === userId;
      })
      .map((row) => requestFromRow(row, aux.categories, aux.statusLabels));
  });
  return cached.data;
}

/**
 * Актуальный гейт телефона пользователя по его последней заявке: сервер
 * пересчитывает метки по ТЕКУЩЕМУ профилю автора при каждом чтении строки
 * (markettake.AnnotatePhoneVerification). Совпадение номера заявки с
 * переданным phone позволяет не показывать устаревший статус после смены
 * телефона. Нет заявок/метки — 'unknown' (честно, без «подтверждён»).
 */
export async function loadMyMarketPhoneGate(userId: string, phone = ''): Promise<MarketPhoneGate> {
  if (!userId) return { state: 'unknown', hint: '' };
  const wanted = normalizePhoneDigits(phone);
  const rows = await listTableRows(MARKET_REQUEST_TABLE, {
    pageSize: 10,
    orderBy: 'created_at',
    orderDesc: true,
    filters: [{ column: 'created_by', value: userId }],
  });
  for (const row of rows) {
    const cells = cellsOf(row);
    const rowPhone = normalizePhoneDigits(cells.get('phone') || '');
    if (wanted && rowPhone !== wanted) continue;
    const state: MarketPhoneState = isTrue(cells.get('phone_verified'))
      ? 'verified'
      : isTrue(cells.get('phone_unverified'))
        ? 'unverified'
        : 'unknown';
    return { state, hint: cells.get('phone_verification_hint') || '' };
  }
  return { state: 'unknown', hint: '' };
}

/** Заявка по id (для карточки; данные — из кэша чтения при обрыве связи). */
export async function loadMarketRequest(requestId: string): Promise<MarketRequest> {
  if (!requestId) throw new Error('Не указана заявка');
  const cached = await readThrough<MarketRequest>(`market:request:${requestId}`, async () => {
    const aux = await auxiliaryData();
    const rows = await listTableRows(MARKET_REQUEST_TABLE, {
      pageSize: 1,
      filters: [{ column: 'id', value: requestId }],
    });
    const row = rows[0];
    if (!row) throw new Error('Заявка не найдена или недоступна');
    return requestFromRow(row, aux.categories, aux.statusLabels);
  });
  return cached.data;
}

/** Фото заявки (file_links: владелец = id заявки). */
export async function loadMarketRequestPhotos(requestId: string): Promise<MarketPhoto[]> {
  if (!requestId) return [];
  const cached = await readThrough<MarketPhoto[]>(`market:photos:${requestId}`, async () => {
    const res: any = await getSDK().files.listFiles({ ownerId: requestId, includeLinked: false });
    return ((res.items || []) as any[])
      .filter((f) => {
        const type = String(f.fileType || '');
        const name = String(f.fileName || '').toLowerCase();
        return type.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif|bmp)$/i.test(name);
      })
      .map((f) => ({
        id: String(f.id || ''),
        fileName: String(f.fileName || 'Фото'),
        fileType: String(f.fileType || ''),
        fileSize: Number(f.fileSize || 0),
      }))
      .filter((f) => f.id);
  });
  return cached.data;
}

/** Причина сбоя — человеческая и с явным видом (права/модуль/сеть/прочее). */
export function toMarketFailure(e: unknown): MarketFailure {
  if (isPermissionDeniedError(e)) {
    return {
      kind: 'permission',
      message:
        'Недостаточно прав для работы с заявками. Администратор базы должен выдать роли право '
        + '«Добавление/Чтение» на документ «Заявка (маркетплейс)» (mkt_requests).',
    };
  }
  if (isNotFoundError(e)) {
    return {
      kind: 'not-installed',
      message:
        'Модуль «Маркетплейс» не установлен в этой базе (нет документа mkt_requests и справочника '
        + 'категорий). Установите поставку hydra.market.',
    };
  }
  if (isTransportError(e)) {
    return { kind: 'offline', message: friendlyError(e) };
  }
  const message = friendlyError(e).trim();
  return { kind: 'other', message: message || 'Не удалось выполнить действие' };
}

/** Название заявки для заголовков («Заявка №…»). */
export function marketRequestTitle(request: Pick<MarketRequest, 'number' | 'descr'>): string {
  if (request.number) return `Заявка № ${request.number}`;
  const firstLine = (request.descr || '').split('\n').map((s) => s.trim()).find(Boolean);
  return firstLine ? firstLine.slice(0, 60) : 'Заявка';
}
